import { cn } from './cn';
import { Icon, type IconName } from './Icon';

/**
 * Tab-style switch. The highlighted thumb slides between options (pure CSS: it is one element
 * positioned by the selected index), so changing option shows where the selection moved.
 */
/**
 * The one tab switch used everywhere (search type, tracking mode, tickets…): a rounded track with a
 * green thumb that slides to the selected option. One element positioned by index, so it is pure CSS.
 * Pass `idPrefix` when the tabs control a panel, to wire up aria-controls / aria-labelledby.
 */
export function Segmented<T extends string | number>({ options, value, onChange, className, ariaLabel, idPrefix, variant = 'solid' }: {
  options: { value: T; label: string; icon?: IconName }[]; value: T; onChange: (v: T) => void; className?: string; ariaLabel?: string; idPrefix?: string;
  /** `solid` (default): green thumb. `soft`: quieter white thumb, for a switch nested inside a card that already has tabs. */
  variant?: 'solid' | 'soft';
}) {
  const soft = variant === 'soft';
  const index = Math.max(0, options.findIndex((o) => o.value === value));
  const n = options.length;
  return (
    <div role="tablist" aria-label={ariaLabel} className={cn('relative grid auto-cols-fr grid-flow-col gap-1 bg-fill', soft ? 'rounded-md p-1' : 'rounded-[16px] p-1.5 ring-1 ring-line', className)}>
      <span
        aria-hidden
        className={cn('pointer-events-none absolute transition-transform duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] rtl:[transform:translateX(calc(var(--i)*(100%_+_0.25rem)*-1))] ltr:[transform:translateX(calc(var(--i)*(100%_+_0.25rem)))]', soft ? 'inset-y-1 start-1 rounded-[10px] bg-paper shadow-card' : 'inset-y-1.5 start-1.5 rounded-md bg-cta shadow-glow')}
        style={{ width: `calc((100% - ${soft ? 0.5 : 0.75}rem - ${(n - 1) * 0.25}rem) / ${n})`, ['--i' as string]: index }}
      />
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={String(o.value)}
            role="tab"
            type="button"
            id={idPrefix ? `${idPrefix}-tab-${o.value}` : undefined}
            aria-controls={idPrefix ? `${idPrefix}-panel-${o.value}` : undefined}
            aria-selected={on}
            onClick={() => onChange(o.value)}
            className={cn('relative flex items-center justify-center gap-2 px-2 text-sm font-bold transition-colors active:scale-[0.98]', soft ? 'min-h-10 rounded-[10px] py-2' : 'min-h-11 rounded-md py-2.5', on ? (soft ? 'text-navy' : 'text-white') : 'text-ink-2 hover:text-navy')}
          >
            {o.icon && <Icon name={o.icon} size={18} className={cn('shrink-0 transition-transform duration-300', on && 'scale-110')} />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Counter({ value, onChange, min = 0, max = 10, label }: { value: number; onChange: (n: number) => void; min?: number; max?: number; label: string }) {
  const btn = 'grid size-11 place-items-center sm:size-9 rounded-full border border-line bg-paper text-lg font-bold text-navy transition hover:border-turquoise/50 hover:bg-fill active:scale-90 disabled:opacity-40';
  return (
    <div className="flex items-center gap-2 sm:gap-3" role="group" aria-label={label}>
      <button type="button" className={btn} onClick={() => onChange(value + 1)} disabled={value >= max} aria-label={`افزایش ${label}`}>+</button>
      <span className="min-w-6 text-center text-base font-bold tabular-nums" aria-live="polite">{value}</span>
      <button type="button" className={btn} onClick={() => onChange(value - 1)} disabled={value <= min} aria-label={`کاهش ${label}`}>−</button>
    </div>
  );
}

export function GenderToggle({ value, onChange, labels = { male: 'مرد', female: 'زن' } }: { value: 'مرد' | 'زن' | ''; onChange: (v: 'مرد' | 'زن') => void; labels?: { male: string; female: string } }) {
  return (
    <div role="radiogroup" aria-label="جنسیت" className="grid grid-cols-2 gap-2">
      {(['مرد', 'زن'] as const).map((g) => (
        <button key={g} type="button" role="radio" aria-checked={value === g} onClick={() => onChange(g)}
          className={cn('min-h-11 rounded-md border px-3 py-2.5 text-sm font-bold transition', value === g ? 'border-transparent bg-cta text-white shadow-glow' : 'border-line bg-paper text-ink-2 hover:bg-fill')}>
          {g === 'مرد' ? labels.male : labels.female}
        </button>
      ))}
    </div>
  );
}
